import * as React from 'react'

import { cn } from '@/registry/reapit/lib/utils'
import { Flex, Stack } from '@/registry/reapit/ui/flex'
import { TextMuted, TextHeading } from '@/registry/reapit/ui/text'

type PageHeaderProps = React.ComponentProps<'div'> & {
  title: React.ReactNode
  overline?: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
  container?: 'fluid' | 'wide' | 'narrow'
}

const containerClasses: Record<NonNullable<PageHeaderProps['container']>, string> = {
  fluid: 'max-w-none',
  wide: 'max-w-7xl mx-auto',
  narrow: 'max-w-3xl mx-auto',
}

function PageHeader({
  className,
  title,
  overline,
  description,
  actions,
  container = 'fluid',
  ...props
}: PageHeaderProps) {
  return (
    <div data-slot="page-header" className={cn('w-full', className)} {...props}>
      <div className={cn('px-4 py-6 sm:px-6 sm:py-8', containerClasses[container])}>
        {/*
         * DS: Page header layout — title area left, actions right.
         * On narrow viewports actions stack below the title area.
         * Uses Flex/Stack layout primitives (no raw div className="flex …").
         */}
        <Flex align="start" between className="flex-col gap-4 sm:flex-row">
          <Stack gap={1} data-slot="page-header-title-area">
            {overline && (
              // DS: overline uses secondary/muted colour, S size (14px)
              <TextMuted data-slot="page-header-overline" block>
                {overline}
              </TextMuted>
            )}

            {/*
             * DS: page title uses 2XL heading (28px semi-bold) per Principal typography decision.
             * On wider viewports this steps up to 2XL (28px) via responsive class.
             */}
            <h1
              data-slot="page-header-title"
              className="text-2xl font-semibold tracking-tight text-foreground md:text-[1.75rem]"
            >
              {title}
            </h1>

            {description && (
              // DS: description line uses muted secondary colour, S size (14px)
              <TextMuted data-slot="page-header-description" block>
                {description}
              </TextMuted>
            )}
          </Stack>

          {actions && (
            // DS: page-level actions sit at the right, vertically centred with the title row
            <Flex data-slot="page-header-actions" align="center" gap={2} className="shrink-0 sm:mt-1">
              {actions}
            </Flex>
          )}
        </Flex>
      </div>
    </div>
  )
}

export { PageHeader }
export type { PageHeaderProps }
